import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '@/services/api';
import { getStatusColor } from '@/utils/helpers';
import toast from 'react-hot-toast';

const StaffStudents = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const classes = ['', 'JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'];

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const params = {};
        if (selectedClass) params.class = selectedClass;
        if (search) params.search = search;
        const res = await adminAPI.getStudents(params);
        setStudents(res.data.data || []);
      } catch (err) {
        console.error('Students fetch error:', err);
        toast.error('Failed to load students');
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [selectedClass, search]);


const handlePrintPDF = async (student) => {
  try {
    toast.loading('Preparing profile...', { id: 'pdf' });
    
    // Fetch FULL student data including parent details
    const res = await adminAPI.getStudentById(student.id);
    const fullStudent = res.data.data;
    
    toast.dismiss('pdf');
    
    const studentName = fullStudent?.full_name || 
      `${fullStudent?.first_name || ''} ${fullStudent?.last_name || ''}`.trim();
    const admissionNo = fullStudent?.admission_number || 'N/A';
    const photoUrl = fullStudent?.picture || fullStudent?.photo || 
                     fullStudent?.profile_picture || null;
    const initial = studentName.charAt(0).toUpperCase();
    const printDate = new Date().toLocaleDateString('en-GB', {
      day:'2-digit', month:'long', year:'numeric'
    });
    const dob = fullStudent?.date_of_birth 
      ? new Date(fullStudent.date_of_birth).toLocaleDateString('en-GB', {
          day:'2-digit', month:'long', year:'numeric'
        }) 
      : 'N/A';
    const enrollDate = fullStudent?.created_at
      ? new Date(fullStudent.created_at).toLocaleDateString('en-GB', {
          day:'2-digit', month:'long', year:'numeric'
        })
      : 'N/A';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Student Card - ${studentName}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body {
    font-family: 'Segoe UI', Roboto, Arial, sans-serif;
    font-size: 14px;
    color: #222;
    background: #ffffff;
    padding: 20px;
    line-height: 1.6;
  }
  .card {
    max-width: 750px;
    margin: 0 auto;
    border: 3px solid #1a5c38;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(26, 92, 56, 0.15);
  }

  /* HEADER */
  .header {
    background: linear-gradient(135deg, #1a5c38 0%, #2d7a4f 100%);
    color: white;
    text-align: center;
    padding: 20px 24px;
  }
  .header .arabic { 
    font-size: 18px; 
    margin-bottom: 6px; 
    opacity: 0.95;
    font-weight: 500;
  }
  .header h1 { 
    font-size: 22px; 
    font-weight: 800; 
    margin-bottom: 3px;
    letter-spacing: 0.5px;
  }
  .header .tagline { 
    font-size: 13px; 
    opacity: 0.9; 
    margin-bottom: 10px;
  }
  .header h2 {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 3px;
    text-transform: uppercase;
    background: rgba(255,255,255,0.2);
    display: inline-block;
    padding: 6px 22px;
    border-radius: 25px;
    margin-top: 6px;
  }

  /* PERSONAL SECTION */
  .personal {
    display: flex;
    gap: 24px;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    background: #fcfdfc;
  }
  .photo-box {
    flex-shrink: 0;
    width: 110px; 
    height: 135px;
    border: 3px solid #1a5c38;
    border-radius: 8px;
    overflow: hidden;
    background: #e8f5e9;
    display: flex; 
    align-items: center; 
    justify-content: center;
    box-shadow: 0 2px 8px rgba(26, 92, 56, 0.1);
  }
  .photo-box img { 
    width:100%; 
    height:100%; 
    object-fit:cover; 
  }
  .photo-initial { 
    font-size: 52px; 
    font-weight: 900; 
    color: #1a5c38; 
    opacity: 0.35; 
  }
  .photo-label {
    text-align: center;
    font-size: 11px;
    color: #6b7280;
    margin-top: 6px;
    font-weight: 500;
  }
  .photo-col { 
    display: flex; 
    flex-direction: column; 
    align-items: center;
  }
  .details { flex: 1; }
  .section-label {
    background: linear-gradient(135deg, #1a5c38 0%, #2d7a4f 100%);
    color: white;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.8px;
    padding: 6px 14px;
    border-radius: 6px;
    margin-bottom: 12px;
    display: inline-block;
    box-shadow: 0 2px 4px rgba(26, 92, 56, 0.2);
  }
  .field { 
    display: flex; 
    margin-bottom: 8px;
    line-height: 1.6;
  }
  .field-label { 
    font-weight: 700; 
    min-width: 130px; 
    color: #374151;
    font-size: 13px;
  }
  .field-value { 
    color: #111827; 
    font-size: 13px;
    font-weight: 500;
  }
  .field-value.na { 
    color: #9ca3af; 
    font-style: italic; 
  }

  /* OTHER SECTIONS */
  .section {
    padding: 16px 24px;
    border-bottom: 1px solid #e5e7eb;
  }
  .section:last-of-type { border-bottom: none; }
  .section-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 20px;
    margin-top: 10px;
  }
  .section-grid .field { margin-bottom: 6px; }

  /* SIGNATURE */
  .signature-area {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 20px 24px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
  }
  .sig-box { 
    text-align: center; 
    width: 220px; 
  }
  .sig-line { 
    border-top: 2px solid #4b5563; 
    padding-top: 8px; 
    font-size: 12px; 
    color: #4b5563; 
    margin-top: 35px;
    font-weight: 500;
  }

  /* FOOTER */
  .footer {
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    border-top: 1px solid #d1d5db;
    text-align: center;
    padding: 10px;
    font-size: 11px;
    color: #6b7280;
    font-weight: 500;
  }

  @media print {
    body { padding: 0; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .card { box-shadow: none; }
  }
</style>
</head>
<body>
<div class="card">

  <!-- HEADER -->
  <div class="header">
    <div class="arabic">بسم الله الرحمن الرحيم</div>
    <h1>DAR-UL-HIKMAH COLLEGE</h1>
    <div class="tagline">of Islamic and Arabic Studies</div>
    <h2>Student Information Card</h2>
  </div>

  <!-- PERSONAL DETAILS -->
  <div class="personal">
    <div class="details">
      <span class="section-label">Personal Details</span>
      <div class="field">
        <span class="field-label">Full Name:</span>
        <span class="field-value">${studentName || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Admission No:</span>
        <span class="field-value">${admissionNo}</span>
      </div>
      <div class="field">
        <span class="field-label">Email:</span>
        <span class="field-value">${fullStudent?.email || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Class:</span>
        <span class="field-value">${fullStudent?.class || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Date of Birth:</span>
        <span class="field-value">${dob}</span>
      </div>
      <div class="field">
        <span class="field-label">Gender:</span>
        <span class="field-value">${fullStudent?.gender || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Status:</span>
        <span class="field-value">${(fullStudent?.status || '—').toUpperCase()}</span>
      </div>
    </div>
    <div class="photo-col">
      <div class="photo-box">
        ${photoUrl 
          ? `<img src="${photoUrl}" alt="Photo" />`
          : `<div class="photo-initial">${initial}</div>`
        }
      </div>
      <div class="photo-label">Passport Photo</div>
    </div>
  </div>

  <!-- PARENT/GUARDIAN -->
  <div class="section">
    <span class="section-label">Parent / Guardian Information</span>
    <div class="section-grid">
      <div class="field">
        <span class="field-label">Guardian Name:</span>
        <span class="field-value">${fullStudent?.parent_name || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Phone:</span>
        <span class="field-value">${fullStudent?.parent_phone || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Guardian Email:</span>
        <span class="field-value">${fullStudent?.parent_email || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Relationship:</span>
        <span class="field-value">${fullStudent?.relationship || '—'}</span>
      </div>
    </div>
    <div class="field" style="margin-top:4px">
      <span class="field-label">Address:</span>
      <span class="field-value">${fullStudent?.home_address || '—'}</span>
    </div>
  </div>

  <!-- ENROLLMENT -->
  <div class="section">
    <span class="section-label">Enrollment Information</span>
    <div class="section-grid" style="margin-top:8px">
      <div class="field">
        <span class="field-label">Enrollment Date:</span>
        <span class="field-value">${enrollDate}</span>
      </div>
      <div class="field">
        <span class="field-label">Payment Status:</span>
        <span class="field-value">${fullStudent?.payment_status || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Session:</span>
        <span class="field-value">${fullStudent?.session || '—'}</span>
      </div>
      <div class="field">
        <span class="field-label">Previous School:</span>
        <span class="field-value">${fullStudent?.previous_school || '—'}</span>
      </div>
    </div>
  </div>

  <!-- SIGNATURE -->
  <div class="signature-area">
    <div class="sig-box">
      <div class="sig-line">Principal's Signature & Stamp</div>
    </div>
    <div class="sig-box">
      <div class="sig-line">${printDate}<br/>Date Printed</div>
    </div>
  </div>

  <!-- FOOTER -->
  <div class="footer">
    This document is computer generated by Dar-ul-Hikmah College Management System
  </div>

</div>
<script>
  window.onload = function() {
    setTimeout(function() { window.print(); }, 600);
  };
</script>
</body>
</html>`);
  printWindow.document.close();
} catch (err) {
  toast.dismiss('pdf');
  toast.error('Failed to load student data');
  console.error(err);
}
};

  if (loading) {

    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-slate-800">Students</h2>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Search</label>
          <div className="relative">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or admission number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all text-slate-800 font-medium"
            />
          </div>
        </div>
        <div className="sm:w-48">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Class</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all text-slate-800 font-medium"
          >
            <option value="">All Classes</option>
            {classes.slice(1).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Student</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Admission No</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Class</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Phone</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {students.length > 0 ? students.map((student) => (
                <tr key={student.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold text-sm">
                        {(student.full_name || 'S').charAt(0)}
                      </div>
                      <p className="font-semibold text-slate-800 text-sm">{student.full_name || '—'}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 font-mono">{student.admission_number || '—'}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{student.class || '—'}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{student.email || '—'}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{student.phone || '—'}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(student.payment_status)}`}>
                      {student.payment_status || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/admin/students/${student.id}/edit`)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        title="Edit Profile"
                      >
                        <i className="fas fa-edit text-sm" />
                      </button>
                      <button
                        onClick={() => handlePrintPDF(student)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors"
                        title="Print Profile"
                      >
                        <i className="fas fa-print text-sm" />
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedStudent(student);
                          setShowDeleteModal(true);
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                        title="Delete Student"
                      >
                        <i className="fas fa-trash text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-slate-400">
                    <i className="fas fa-inbox text-3xl mb-3 block" />
                    <p className="text-sm">No students found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedStudent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Delete Student</h3>
              <p className="text-slate-500">
                Are you sure you want to permanently delete 
                <strong>{selectedStudent.full_name} ({selectedStudent.admission_number})?</strong> 
                This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-6 py-3 rounded-xl font-medium text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await adminAPI.deleteStudent(selectedStudent.id);
                    toast.success('Student deleted successfully');
                    setShowDeleteModal(false);
                    const res = await adminAPI.getStudents({ search, class: selectedClass });
                    setStudents(res.data.data || []);
                  } catch (err) {
                    toast.error('Failed to delete student');
                  }
                }}
                className="px-6 py-3 rounded-xl font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffStudents;

